import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Typography,
  useTheme,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import apiClient from '../api/client';
import { getInvoice, type Invoice } from '../api/po';

export interface CalendarEventItem {
  invoice_id: number;
  title: string;
  date: string;
  currency?: string | null;
  payment_terms?: string | null;
  status?: string | null;
  client_name?: string | null;
  est_value_original?: number | null;
  invoiced_value_original?: number | null;
  operational_revenue_value?: number | null;
  payment_received_value?: number | null;
  fx_rate_inr?: number | null;
  fx_rate_usd?: number | null;
}

export interface StatusCounts {
  Planned: number;
  Invoiced: number;
  Received: number;
  Cancelled: number;
  Delayed: number;
  Other: number;
}

export type Months = {
  January: CalendarEventItem[];
  February: CalendarEventItem[];
  March: CalendarEventItem[];
  April: CalendarEventItem[];
  May: CalendarEventItem[];
  June: CalendarEventItem[];
  July: CalendarEventItem[];
  August: CalendarEventItem[];
  September: CalendarEventItem[];
  October: CalendarEventItem[];
  November: CalendarEventItem[];
  December: CalendarEventItem[];
};

export interface YearData {
  /** API returns fiscal label, e.g. `"FY 2025-26"` */
  year: string;
  months: Months;
  status_counts?: StatusCounts;
}

export interface InvoiceCalendarResponse {
  [year: string]: YearData;
}

/** Strip / merge order matches API `months` (Indian FY: April → March). */
const FISCAL_MONTH_ORDER = [
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
  'January',
  'February',
  'March',
] as const;

const MONTH_SHORT: Record<string, string> = {
  January: 'Jan', February: 'Feb', March: 'Mar', April: 'Apr', May: 'May', June: 'Jun',
  July: 'Jul', August: 'Aug', September: 'Sep', October: 'Oct', November: 'Nov', December: 'Dec',
};

const YEAR_OPTIONS = Array.from({ length: 9 }, (_, i) => String(2028 - i));
const STATUS_DISPLAY_ORDER: Array<Exclude<keyof StatusCounts, 'Other'>> = [
  'Planned',
  'Invoiced',
  'Received',
  'Cancelled',
  'Delayed',
];
const STATUS_CARD_COLORS: Record<Exclude<keyof StatusCounts, 'Other'>, { bg: string; border: string; text: string }> = {
  Planned: { bg: '#E8F0FE', border: '#A8C4FA', text: '#1E5BC6' },
  Invoiced: { bg: '#E8F4FD', border: '#9ED0F6', text: '#1168B3' },
  Received: { bg: '#EAF7EE', border: '#A9DFB8', text: '#1F7A39' },
  Cancelled: { bg: '#FDECEC', border: '#F0B4B4', text: '#C62828' },
  Delayed: { bg: '#FFF7E6', border: '#F3D08A', text: '#9A6700' },
};
const EMPTY_STATUS_COUNTS: StatusCounts = {
  Planned: 0,
  Invoiced: 0,
  Received: 0,
  Cancelled: 0,
  Delayed: 0,
  Other: 0,
};

/** Month-card event row: border / accent by invoice status */
type InvoiceEventStatusKey = 'Planned' | 'Invoiced' | 'Received' | 'Cancelled' | 'Delayed' | 'default';

const EVENT_STATUS_BORDER: Record<InvoiceEventStatusKey, string> = {
  Planned: '#1E88E5',
  Invoiced: '#43A047',
  Received: '#00897B',
  Cancelled: '#E53935',
  Delayed: '#FB8C00',
  default: 'rgba(15, 23, 42, 0.12)',
};

const EVENT_STATUS_BG: Record<InvoiceEventStatusKey, string> = {
  Planned: 'rgba(30, 136, 229, 0.08)',
  Invoiced: 'rgba(67, 160, 71, 0.08)',
  Received: 'rgba(0, 137, 123, 0.08)',
  Cancelled: 'rgba(229, 57, 53, 0.08)',
  Delayed: 'rgba(251, 140, 0, 0.1)',
  default: 'rgba(15, 23, 42, 0.03)',
};

const INVOICE_STATUS_CHIP_SX = {
  fontWeight: 600,
  fontSize: { xs: '0.72rem', sm: '0.78rem', md: '0.82rem' },
  minHeight: { xs: 24, sm: 26, md: 28 },
  height: 'auto',
  py: 0.5,
  maxWidth: '100%',
} as const;

function resolveEventStatusKey(status: string | null | undefined): InvoiceEventStatusKey {
  const s = (status ?? '').trim();
  if (!s) return 'default';
  if (s === 'Canceled') return 'Cancelled';
  if (
    s === 'Planned' ||
    s === 'Invoiced' ||
    s === 'Received' ||
    s === 'Cancelled' ||
    s === 'Delayed'
  ) {
    return s;
  }
  return 'default';
}

/** Same palette as month-card event row (border + tint + readable label). */
function getInvoiceStatusBadgePalette(status: string | null | undefined): {
  border: string;
  bg: string;
  text: string;
} {
  const key = resolveEventStatusKey(status);
  if (key === 'default') {
    return {
      border: EVENT_STATUS_BORDER.default,
      bg: EVENT_STATUS_BG.default,
      text: 'rgba(51, 65, 85, 0.88)',
    };
  }
  return {
    border: EVENT_STATUS_BORDER[key],
    bg: EVENT_STATUS_BG[key],
    text: STATUS_CARD_COLORS[key].text,
  };
}

function invoiceEventStatusBadgeSx(status: string | null | undefined) {
  const p = getInvoiceStatusBadgePalette(status);
  return {
    ...INVOICE_STATUS_CHIP_SX,
    bgcolor: p.bg,
    color: p.text,
    border: `1px solid ${p.border}`,
    fontWeight: 600,
    '& .MuiChip-label': {
      px: 1.25,
      py: 0.25,
      whiteSpace: 'normal',
      lineHeight: 1.35,
      display: 'block',
      textAlign: 'left',
    },
  } as const;
}

/** Tiny capsule badge for month-card event rows (dialog uses `invoiceEventStatusBadgeSx`). */
function invoiceMonthCardStatusBadgeSx(status: string | null | undefined) {
  const p = getInvoiceStatusBadgePalette(status);
  return {
    bgcolor: p.bg,
    color: p.text,
    border: `1px solid ${p.border}`,
    fontWeight: 700,
    fontSize: '0.625rem',
    letterSpacing: 0.02,
    height: 20,
    minHeight: 20,
    maxHeight: 20,
    borderRadius: 9999,
    flexShrink: 0,
    maxWidth: 'min(100%, 7.5rem)',
    '& .MuiChip-label': {
      px: 0.625,
      py: 0,
      lineHeight: 1.15,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  } as const;
}

function partitionMonthEvents(events: CalendarEventItem[]): {
  planned: CalendarEventItem[];
  invoiced: CalendarEventItem[];
} {
  const planned: CalendarEventItem[] = [];
  const invoiced: CalendarEventItem[] = [];
  const byDate = (a: CalendarEventItem, b: CalendarEventItem) => a.date.localeCompare(b.date);

  for (const e of events) {
    const key = resolveEventStatusKey(e.status);
    if (key === 'Invoiced' || key === 'Received') {
      invoiced.push(e);
    } else if (key === 'Planned' || key === 'Cancelled' || key === 'Delayed') {
      planned.push(e);
    } else {
      planned.push(e);
    }
  }
  planned.sort(byDate);
  invoiced.sort(byDate);
  return { planned, invoiced };
}

/** MUI `gap` in px on xs (spacing 1.5 → 12px) for flex gap total in one-card-per-view math */
const XS_MONTH_STRIP_GAP_PX = 12;
const XS_MONTH_CARD_FLEX =
  FISCAL_MONTH_ORDER.length > 0
    ? `0 0 calc((100% - ${(FISCAL_MONTH_ORDER.length - 1) * XS_MONTH_STRIP_GAP_PX}px) / ${FISCAL_MONTH_ORDER.length})`
    : '0 0 auto';

function formatEventDate(isoDate: string): string {
  try {
    const d = new Date(isoDate + 'T12:00:00');
    return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  } catch {
    return isoDate;
  }
}

function formatFiscalYearLabel(startYear: string): string {
  const start = Number(startYear);
  if (!Number.isFinite(start)) return `FY ${startYear}`;
  const endShort = String((start + 1) % 100).padStart(2, '0');
  return `FY ${start}-${endShort}`;
}

/** `selectedYear` from UI is calendar start year of FY (e.g. `2025` → FY 2025-26). */
function getYearBucket(data: InvoiceCalendarResponse, calendarStartYear: string): YearData | undefined {
  const fyKey = formatFiscalYearLabel(calendarStartYear);
  if (data[fyKey]) return data[fyKey];
  if (data[calendarStartYear]) return data[calendarStartYear];
  const keys = Object.keys(data);
  if (keys.length === 1) return data[keys[0]];
  return undefined;
}

/** True when `d` falls in FY that starts April of `fyStartYear` (same convention as year query param). */
function isDateInFiscalYear(d: Date, fyStartYear: number): boolean {
  const y = d.getFullYear();
  const m = d.getMonth();
  if (m >= 3) return y === fyStartYear;
  return y === fyStartYear + 1;
}

/** Map JS month (0=Jan … 11=Dec) to index in `FISCAL_MONTH_ORDER` (April=0 … March=11). */
function calendarMonthToFiscalStripIndex(jsMonthZero: number): number {
  return (jsMonthZero + 9) % 12;
}

function formatInvoicedValueLine(value: number | null | undefined, currency: string | null | undefined): string {
  if (value === null || value === undefined) return 'Invoiced Value: -';
  const numeric = Number(value);
  const formatted = Number.isFinite(numeric) ? numeric.toFixed(1) : String(value);
  return `Invoiced Value: ${formatted}${currency ? ` ${String(currency).toUpperCase()}` : ''}`;
}

function formatCurrency(value: number | null, currency: string | null): string {
  if (value === null || value === undefined) return '-';
  const currencyUpper = currency ? currency.toUpperCase() : '';
  const currencySymbol =
    currencyUpper === 'USD' ? '$' : currencyUpper === 'EUR' ? '€' : currencyUpper === 'INR' ? '₹' : '';
  return `${currencySymbol}${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function calculateUSDValue(originalValue: number | null, fxRateUSD: number | null): number | null {
  if (originalValue === null || originalValue === undefined || fxRateUSD === null || fxRateUSD === undefined) {
    return null;
  }
  return originalValue * fxRateUSD;
}

function calculateINRValue(originalValue: number | null, fxRateINR: number | null): number | null {
  if (originalValue === null || originalValue === undefined || fxRateINR === null || fxRateINR === undefined) {
    return null;
  }
  return originalValue * fxRateINR;
}

function formatDetailDate(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return dateString;
  }
}

function emptyMonths(): Record<string, CalendarEventItem[]> {
  return FISCAL_MONTH_ORDER.reduce((acc, m) => {
    acc[m] = [];
    return acc;
  }, {} as Record<string, CalendarEventItem[]>);
}

const InvoiceCalendarPage: React.FC = () => {
  const theme = useTheme();
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonthIndex = now.getMonth();
  const fiscalStripIndexToday = calendarMonthToFiscalStripIndex(currentMonthIndex);

  const [selectedYear, setSelectedYear] = useState<string>(String(currentYear));
  const [calendarEvents, setCalendarEvents] = useState<InvoiceCalendarResponse>({});
  const [invoicePreviewOpen, setInvoicePreviewOpen] = useState(false);
  const [previewInvoice, setPreviewInvoice] = useState<Invoice | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const fetchInventoryCalendarEvents = useCallback(async (year: string) => {
    try {
      const response = await apiClient.get(`/api/v1/invoices/calender_invoices?year=${year}`);
      setCalendarEvents((response?.data ?? {}) as InvoiceCalendarResponse);
    } catch {
      setCalendarEvents({});
    }
  }, []);

  useEffect(() => {
    fetchInventoryCalendarEvents(selectedYear);
  }, [selectedYear, fetchInventoryCalendarEvents]);

  const monthsData = useMemo(() => {
    const entry = getYearBucket(calendarEvents, selectedYear);
    const merged = emptyMonths();
    if (!entry?.months) return merged;
    FISCAL_MONTH_ORDER.forEach((m) => {
      merged[m] = entry.months[m] ?? [];
    });
    return merged;
  }, [calendarEvents, selectedYear]);
  const statusCounts = useMemo<StatusCounts>(() => {
    const counts = getYearBucket(calendarEvents, selectedYear)?.status_counts;
    if (!counts) return EMPTY_STATUS_COUNTS;
    return { ...EMPTY_STATUS_COUNTS, ...counts };
  }, [calendarEvents, selectedYear]);

  const handleYearChange = (e: SelectChangeEvent<string>) => setSelectedYear(e.target.value);

  const handleOpenInvoicePreview = useCallback(async (ev: CalendarEventItem) => {
    setInvoicePreviewOpen(true);
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewInvoice(null);
    try {
      const inv = await getInvoice(ev.invoice_id);
      setPreviewInvoice(inv);
    } catch {
      setPreviewError('Failed to load invoice details.');
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const handleCloseInvoicePreview = useCallback(() => {
    setInvoicePreviewOpen(false);
    setPreviewInvoice(null);
    setPreviewError(null);
    setPreviewLoading(false);
  }, []);

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'flex-end',
          width: '100%',
          mb: { xs: 3, sm: 3.5 },
        }}
      >
        <FormControl
          size="small"
          sx={{
            minWidth: 160,
            maxWidth: 200,
            width: { xs: 'min(100%, 200px)', sm: 200 },
            flexShrink: 0,
          }}
        >
          <InputLabel id="invoice-cal-year-label">Year</InputLabel>
          <Select labelId="invoice-cal-year-label" label="Year" value={selectedYear} onChange={handleYearChange}>
            {YEAR_OPTIONS.map((y) => (
              <MenuItem key={y} value={y}>
                {formatFiscalYearLabel(y)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={{ xs: 1.25, sm: 1.5 }} sx={{ mb: { xs: 2, sm: 2.5 } }}>
        {STATUS_DISPLAY_ORDER.map((status) => {
          const palette = STATUS_CARD_COLORS[status];
          return (
            <Grid item xs={6} sm={4} md={3} lg key={status}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: 1,
                  borderColor: palette.border,
                  bgcolor: palette.bg,
                  height: '100%',
                }}
              >
                <CardContent sx={{ p: { xs: 1.25, sm: 1.5 }, '&:last-child': { pb: { xs: 1.25, sm: 1.5 } } }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}
                  >
                    {status}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      mt: 0.5,
                      fontWeight: 800,
                      color: palette.text,
                      fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                      lineHeight: 1.2,
                    }}
                  >
                    {statusCounts[status] ?? 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Box
        sx={{
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          overflowX: 'auto',
          overflowY: 'hidden',
          WebkitOverflowScrolling: 'touch',
          pb: 1.5,
          scrollbarGutter: 'stable',
          '&::-webkit-scrollbar': { height: 8 },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'action.disabled',
            borderRadius: 4,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            alignItems: 'stretch',
            gap: { xs: 1.5, sm: 2 },
            // xs: track width = N × viewport strip → each card ≈ one full visible column (one month per screen)
            width: { xs: `${FISCAL_MONTH_ORDER.length * 100}%`, sm: 'max-content' },
            scrollSnapType: { xs: 'x mandatory', sm: 'x proximity' },
            pr: { xs: 0.5, sm: 0 },
          }}
        >
          {FISCAL_MONTH_ORDER.map((monthName, idx) => {
            const events = monthsData[monthName] ?? [];
            const fyStart = Number(selectedYear);
            const isCurrentFy = Number.isFinite(fyStart) && isDateInFiscalYear(now, fyStart);
            const isCurrentMonth = isCurrentFy && idx === fiscalStripIndexToday;
            return (
              <MonthCard
                key={monthName}
                monthName={monthName}
                monthShort={MONTH_SHORT[monthName]}
                events={events}
                isCurrentMonth={isCurrentMonth}
                theme={theme}
                onEventClick={handleOpenInvoicePreview}
              />
            );
          })}
        </Box>
      </Box>

      <InvoicePreviewDialog
        open={invoicePreviewOpen}
        onClose={handleCloseInvoicePreview}
        invoice={previewInvoice}
        loading={previewLoading}
        error={previewError}
      />
    </Box>
  );
};

/** sm+: fixed card width. xs: flex basis from strip math (one card ≈ full scroll area width). */
const MONTH_CARD_WIDTH_SX = {
  flex: { xs: XS_MONTH_CARD_FLEX, sm: '0 0 auto' },
  width: { sm: 328, md: 352, lg: 380 },
  minWidth: { xs: 0, sm: 312, md: 336, lg: 360 },
  scrollSnapAlign: 'start' as const,
};

type MonthCardProps = {
  monthName: string;
  monthShort: string;
  events: CalendarEventItem[];
  isCurrentMonth: boolean;
  theme: Theme;
  onEventClick: (event: CalendarEventItem) => void;
};

function MonthCard({ monthName, monthShort, events, isCurrentMonth, theme, onEventClick }: MonthCardProps) {
  const { planned, invoiced } = useMemo(() => partitionMonthEvents(events), [events]);

  return (
    <Paper elevation={0} sx={{ ...MONTH_CARD_WIDTH_SX, borderRadius: 2, border: 1, borderColor: isCurrentMonth ? 'primary.main' : 'divider', boxShadow: isCurrentMonth ? `0 1px 4px ${theme.palette.mode === 'dark' ? 'rgba(99,102,241,0.35)' : 'rgba(25, 118, 210, 0.18)'}` : '0 1px 2px rgba(0,0,0,0.06)', bgcolor: isCurrentMonth ? 'action.hover' : 'background.paper', display: 'flex', flexDirection: 'column', height: { xs: '85dvh', sm: '85vh' }, minHeight: { xs: 240, sm: 280 }, maxHeight: { xs: '85dvh', sm: '85vh' }, overflow: 'hidden', alignSelf: 'stretch', boxSizing: 'border-box', transition: 'box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease' }}>
      <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', bgcolor: theme.palette.mode === 'dark' ? (isCurrentMonth ? 'primary.dark' : 'grey.900') : (isCurrentMonth ? 'primary.50' : 'grey.50') }}>
        <Typography variant="subtitle2" fontWeight={700} color="text.primary">{monthShort}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {monthName}
          {isCurrentMonth ? ' · Today' : ''}
        </Typography>
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          p: { xs: 1.25, sm: 1.5 },
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
        }}
      >
        {events.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', py: 1 }}>
            No events
          </Typography>
        ) : (
          <>
            <Box>
              <Typography
                variant="overline"
                sx={{ display: 'block', fontWeight: 700, letterSpacing: 0.08, color: 'text.secondary', mb: 0.75 }}
              >
                Planned
              </Typography>
              {planned.length === 0 ? (
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', pl: 0.25 }}>
                  None
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {planned.map((event) => (
                    <EventRow key={event.invoice_id} event={event} onSelect={() => onEventClick(event)} />
                  ))}
                </Box>
              )}
            </Box>

            <Divider sx={{ borderColor: 'divider' }} />

            <Box>
              <Typography
                variant="overline"
                sx={{ display: 'block', fontWeight: 700, letterSpacing: 0.08, color: 'text.secondary', mb: 0.75 }}
              >
                Invoiced
              </Typography>
              {invoiced.length === 0 ? (
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', pl: 0.25 }}>
                  None
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {invoiced.map((event) => (
                    <EventRow key={event.invoice_id} event={event} onSelect={() => onEventClick(event)} />
                  ))}
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
    </Paper>
  );
}

function EventRow({ event, onSelect }: { event: CalendarEventItem; onSelect: () => void }) {
  const statusKey = resolveEventStatusKey(event.status);
  const accent = EVENT_STATUS_BORDER[statusKey];
  const tint = EVENT_STATUS_BG[statusKey];

  return (
    <Paper
      component="button"
      type="button"
      elevation={0}
      onClick={onSelect}
      aria-label={`View invoice ${event.title}`}
      sx={{
        textAlign: 'left',
        width: '100%',
        p: { xs: 1.25, sm: 1.375 },
        borderRadius: 1.5,
        border: 1,
        borderColor: 'divider',
        bgcolor: tint,
        cursor: 'pointer',
        font: 'inherit',
        color: 'inherit',
        borderBottom: `3px solid ${accent}`,
        boxShadow: 'none',
        transition: 'background-color 0.15s ease, box-shadow 0.15s ease, transform 0.12s ease',
        '@media (hover: hover)': {
          '&:hover': {
            bgcolor: tint,
            filter: 'brightness(0.97)',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          },
        },
        '&:active': { transform: 'scale(0.98)' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 0.75,
        }}
      >
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            fontSize: { xs: '0.8125rem', sm: '0.84rem' },
            lineHeight: 1.45,
            flex: '1 1 auto',
            minWidth: 0,
            pr: 0.25,
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
          }}
        >
          {event.title}
        </Typography>
        <Chip
          label={event.status?.trim() || '-'}
          size="small"
          variant="outlined"
          title={event.status?.trim() || undefined}
          sx={invoiceMonthCardStatusBadgeSx(event.status)}
        />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.72rem' }}>{formatEventDate(event.date)}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.4, fontSize: '0.72rem' }}>
        {formatInvoicedValueLine(event.invoiced_value_original, event.currency)}
      </Typography>
    </Paper>
  );
}

type InvoicePreviewDialogProps = {
  open: boolean;
  onClose: () => void;
  invoice: Invoice | null;
  loading: boolean;
  error: string | null;
};

function InvoicePreviewDialog({ open, onClose, invoice, loading, error }: InvoicePreviewDialogProps) {
  const inv = invoice;
  const planned = inv?.est_value_original ?? null;
  const invoiced = inv?.invoiced_value_original ?? null;
  const fxUsd = inv?.fx_rate_usd ?? null;
  const fxInr = inv?.fx_rate_inr ?? null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: { xs: 2, sm: 2.5 },
          mx: { xs: 1.5, sm: 'auto' },
        },
      }}
    >
      <DialogTitle
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography
            variant="h5"
            component="span"
            sx={{
              fontWeight: 800,
              color: 'primary.main',
              fontSize: 'clamp(1.05rem, 1.6vw, 1.5rem)',
              lineHeight: 1.2,
            }}
          >
            {loading ? 'Invoice' : inv?.invoice_code ?? 'Invoice'}
          </Typography>
          {!loading && inv && (
            <Chip
              label={inv.status || '-'}
              size="small"
              variant="outlined"
              sx={invoiceEventStatusBadgeSx(inv.status)}
            />
          )}
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close"
          sx={{
            width: { xs: 34, sm: 38, md: 40 },
            height: { xs: 34, sm: 38, md: 40 },
            border: 1,
            borderColor: 'divider',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <CloseIcon sx={{ fontSize: { xs: 18, sm: 20, md: 22 } }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 } }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {!loading && inv && (
          <>
            <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 3 }}>
              {planned != null && (
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    elevation={0}
                    sx={{
                      bgcolor: 'primary.50',
                      border: 1,
                      borderColor: 'primary.200',
                      borderRadius: { xs: 1.5, sm: 2 },
                    }}
                  >
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Planned value
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', mt: 0.5, fontSize: 'clamp(1.05rem, 1.4vw, 1.35rem)' }}>
                        {formatCurrency(planned, inv.currency)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {inv.currency || '-'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              {planned != null && fxUsd != null && (
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    elevation={0}
                    sx={{
                      bgcolor: 'success.50',
                      border: 1,
                      borderColor: 'success.200',
                      borderRadius: { xs: 1.5, sm: 2 },
                    }}
                  >
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Planned (USD)
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main', mt: 0.5, fontSize: 'clamp(1.05rem, 1.4vw, 1.35rem)' }}>
                        {formatCurrency(calculateUSDValue(planned, fxUsd), 'USD')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              {planned != null && fxInr != null && (
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    elevation={0}
                    sx={{
                      bgcolor: 'info.50',
                      border: 1,
                      borderColor: 'info.200',
                      borderRadius: { xs: 1.5, sm: 2 },
                    }}
                  >
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Planned (INR)
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'info.main', mt: 0.5, fontSize: 'clamp(1.05rem, 1.4vw, 1.35rem)' }}>
                        {formatCurrency(calculateINRValue(planned, fxInr), 'INR')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              {invoiced != null && (
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    elevation={0}
                    sx={{
                      bgcolor: 'warning.50',
                      border: 1,
                      borderColor: 'warning.200',
                      borderRadius: { xs: 1.5, sm: 2 },
                    }}
                  >
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Invoiced value
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'warning.main', mt: 0.5, fontSize: 'clamp(1.05rem, 1.4vw, 1.35rem)' }}>
                        {formatCurrency(invoiced, inv.currency)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {inv.currency || '-'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>

            <Card elevation={1} sx={{ mb: 3, borderRadius: 2, border: 1, borderColor: 'divider' }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 3,
                    fontWeight: 600,
                    pb: 1,
                    borderBottom: 2,
                    borderColor: 'primary.main',
                    fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                  }}
                >
                  Invoice information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <PreviewField label="Client" value={inv.client_name} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <PreviewField label="Project" value={inv.project_name} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <PreviewField label="PO code" value={`PO-${inv.po_id}`} valueSx={{ color: 'primary.main' }} />
                  </Grid>
                  {inv.description ? (
                    <Grid item xs={12}>
                      <PreviewField label="Description" value={inv.description} />
                    </Grid>
                  ) : null}
                </Grid>
              </CardContent>
            </Card>

            <Card elevation={1} sx={{ borderRadius: 2, border: 1, borderColor: 'divider' }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    fontWeight: 600,
                    pb: 1,
                    borderBottom: 2,
                    borderColor: 'primary.main',
                    fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                  }}
                >
                  Details (read-only)
                </Typography>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}>
                  Planned
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6} md={4}>
                    <PreviewField label="Finance planned date" value={formatDetailDate(inv.finance_planned_date)} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <PreviewField label="Operational planned date" value={formatDetailDate(inv.operational_planned_date)} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <PreviewField label="Service month" value={formatDetailDate(inv.service_month_date)} />
                  </Grid>
                </Grid>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}>
                  Invoiced
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6} md={4}>
                    <PreviewField label="Invoice date" value={formatDetailDate(inv.invoice_date)} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <PreviewField label="Invoiced value (original)" value={formatCurrency(inv.invoiced_value_original, inv.currency)} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <PreviewField label="FX rate (base → USD)" value={inv.fx_rate_usd != null ? String(inv.fx_rate_usd) : '-'} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <PreviewField label="FX rate (base → INR)" value={inv.fx_rate_inr != null ? String(inv.fx_rate_inr) : '-'} />
                  </Grid>
                </Grid>
                {(planned != null && fxUsd != null) || (invoiced != null && fxUsd != null) ? (
                  <Box
                    sx={{
                      mb: 2,
                      p: { xs: 1.5, sm: 2 },
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      border: 1,
                      borderColor: 'grey.300',
                    }}
                  >
                    {planned != null && fxUsd != null && (
                      <Typography variant="body2" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        <strong>Planned:</strong> USD {formatCurrency(calculateUSDValue(planned, fxUsd), 'USD')} | INR{' '}
                        {formatCurrency(calculateINRValue(planned, fxInr), 'INR')}
                      </Typography>
                    )}
                    {invoiced != null && fxUsd != null && (
                      <Typography variant="body2" color="text.secondary" display="block">
                        <strong>Invoiced:</strong> USD {formatCurrency(calculateUSDValue(invoiced, fxUsd), 'USD')} | INR{' '}
                        {formatCurrency(calculateINRValue(invoiced, fxInr), 'INR')}
                      </Typography>
                    )}
                    {inv.payment_received_value != null && inv.payment_received_value !== undefined && fxUsd != null && (
                      <Typography variant="body2" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        <strong>Received:</strong> USD{' '}
                        {formatCurrency(calculateUSDValue(inv.payment_received_value, fxUsd), 'USD')} | INR{' '}
                        {formatCurrency(calculateINRValue(inv.payment_received_value, fxInr), 'INR')}
                      </Typography>
                    )}
                  </Box>
                ) : null}
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}>
                  Received
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6} md={4}>
                    <PreviewField label="Payment terms" value={inv.payment_terms} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <PreviewField label="Payment received date" value={formatDetailDate(inv.payment_received_date)} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <PreviewField
                      label="Payment received value"
                      value={inv.payment_received_value != null ? formatCurrency(inv.payment_received_value, inv.currency) : '-'}
                    />
                  </Grid>
                </Grid>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}>
                  Remarks
                </Typography>
                <PreviewField label="" value={inv.remarks || '-'} />
              </CardContent>
            </Card>
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: { xs: 2, sm: 2.5 },
          borderTop: 1,
          borderColor: 'divider',
          justifyContent: 'flex-end',
        }}
      >
        <Button onClick={onClose} variant="outlined" sx={{ textTransform: 'none', fontWeight: 600, minWidth: 120 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PreviewField({
  label,
  value,
  valueSx,
}: {
  label: string;
  value: string | null | undefined;
  valueSx?: SxProps<Theme>;
}) {
  const display = value === null || value === undefined || value === '' ? '-' : value;
  return (
    <Box sx={{ mb: label ? 2 : 0 }}>
      {label ? (
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}>
          {label}
        </Typography>
      ) : null}
      <Typography variant="body1" fontWeight={600} sx={{ mt: label ? 0.5 : 0, fontSize: { xs: '0.85rem', sm: '0.95rem' }, whiteSpace: 'pre-wrap', ...valueSx }}>
        {display}
      </Typography>
    </Box>
  );
}

export default InvoiceCalendarPage;
